import { htmlToElement } from "@modules/bookmarks/html-parser"
import { addBreakpointListener } from "@modules/util/responsive"
import {
    type EventHighlightListResponse,
    loadInitialEventHighlightCards,
    loadMoreEventHighlightCards,
} from "./event-highlight-api"

import "../event-highlight/grid-gallery.css"
import { Cookie } from "@modules/util/Cookie"

let busyLoadingCards = false
let hasMoreEntries = true

const loadingAnchor: HTMLLIElement = document.createElement("li")
loadingAnchor.classList.add("loading-anchor")

const pageMarker: HTMLLIElement = document.createElement("li")
pageMarker.classList.add("page-marker")

export const eventHighlightSection = (): HTMLElement | null =>
    document.getElementById("event-highlight-section") as HTMLElement | null
const eventHighlightModal = (): HTMLElement | null =>
    document.getElementById("event-highlight-modal") as HTMLElement | null

const galleryToggleButton = (root: HTMLElement | null): HTMLButtonElement | null =>
    (root ?? document)?.querySelector(".close-toggle-button") as HTMLButtonElement | null
const galleryModalBody = (root: HTMLElement | null): HTMLElement | null =>
    (root ?? document)?.querySelector(".modal-body") as HTMLElement | null
const galleryData = (root: HTMLElement | null): HTMLTemplateElement | null =>
    (root ?? document).querySelector(".event-masonry-grid-data") as HTMLTemplateElement | null
const galleryColumns = (root: HTMLElement | null): NodeListOf<HTMLUListElement> =>
    (root ?? document).querySelectorAll<HTMLUListElement>(".masonry-column-grid .event-masonry-grid-column")
const galleryToTopButton = (root: HTMLElement | null): HTMLButtonElement | null =>
    (root ?? document).querySelector<HTMLButtonElement>(".event-to-top-button")

const visibleElementsOnly = (element: HTMLUListElement): boolean =>
    element.offsetParent !== null && element.getBoundingClientRect().width > 0
const toShortestColumn = (a: HTMLUListElement, b: HTMLUListElement): HTMLUListElement =>
    a.offsetHeight > b.offsetHeight ? b : a
const addItemToColumnList = (item: HTMLLIElement, list: HTMLUListElement): void => {
    list.append(item)
}
const distributeItems = (items: Iterable<HTMLLIElement>, columns: HTMLUListElement[]): void => {
    for (const item of items) {
        const shortest = columns.reduce(toShortestColumn)
        shortest.append(item.cloneNode(true))
    }
}

const addTrackingPixelForEventCard = (gridItem: HTMLElement, isEventHighlightClosed: boolean): void => {
    const advertId = Number.parseInt(gridItem.dataset.advertId || "0", 10)
    const placeId = Number.parseInt(gridItem.dataset.placeId || "0", 10)

    if (advertId === 0 || placeId === 0) {
        return
    }

    if (!window.Tracking.isAlreadyTracked(siteConfig.trackingTagsMap.einblendung, advertId, placeId)) {
        window.Tracking.trackBannerEinblendung(advertId, placeId, !isEventHighlightClosed)
    }
}

const initializeMasonryGallery = (root: HTMLElement | null, callback: (() => void) | null = null): void => {
    const data: HTMLTemplateElement | null = galleryData(root)
    let unobserveAnchorCallback = (): void => {}

    if (!data) {
        return
    }

    const eventHighlightCookie: Cookie = !siteConfig.eventHighlightCookie.name
        ? ({} as Cookie)
        : new Cookie(siteConfig.eventHighlightCookie.name, {
              domain: SiteInfo.cookieDomain,
              path: "/",
          })

    const readInitialHighlightData = async (): Promise<void> => {
        if ((galleryData(root)?.content.childElementCount || 0) > 0) {
            return
        }

        const eventData: EventHighlightListResponse = await loadInitialEventHighlightCards()

        hasMoreEntries = eventData.hasMoreEntries

        if (eventData.entries.length === 0) {
            throw new Error()
        }

        eventHighlightSection()?.closest("#event-highlights")?.removeAttribute("hidden")
        addCardsToTemplate(root, eventData)

        callback?.()
    }

    const spreadDataToColumns = (): void => {
        requestAnimationFrame((): void => {
            const columns: NodeListOf<HTMLUListElement> = galleryColumns(root)
            const columnLists: HTMLUListElement[] = Array.from(columns).filter(visibleElementsOnly)
            const listItems: NodeListOf<HTMLLIElement> = data.content.querySelectorAll("li")

            for (const column of columns) {
                column.innerText = ""
            }

            if (columnLists.length === 0) {
                return
            }

            distributeItems(listItems, columnLists)

            const shortestColumn: HTMLUListElement = columnLists.reduce(toShortestColumn)
            addItemToColumnList(loadingAnchor, shortestColumn)
        })
    }

    const addCardsToTemplate = (root: HTMLElement | null, data: EventHighlightListResponse): HTMLLIElement[] => {
        const newItems: HTMLLIElement[] = []

        if (data.entries.length === 0) {
            unobserveAnchorCallback()
            return []
        }

        const marker = pageMarker.cloneNode(true) as HTMLLIElement

        newItems.push(marker)
        galleryData(root)?.content.append(marker)

        const isEventHighlightClosed: boolean =
            eventHighlightCookie.value &&
            eventHighlightCookie.value[siteConfig.eventHighlightCookie.id] === siteConfig.eventHighlightCookie.value

        for (const htmlEntry of data.entries) {
            const element: HTMLLIElement = htmlToElement<HTMLLIElement>(htmlEntry)

            addTrackingPixelForEventCard(element, isEventHighlightClosed)

            newItems.push(element)
            galleryData(root)?.content.append(element)
        }

        if (!data.hasMoreEntries) {
            unobserveAnchorCallback()
        }

        return newItems
    }

    readInitialHighlightData()
        .then((): void => {
            spreadDataToColumns()
            addBreakpointListener((): void => void setTimeout((): void => spreadDataToColumns(), 0))

            if (root === eventHighlightModal()) {
                const modalBody: HTMLElement | null = galleryModalBody(root)
                const scrollObserver = new IntersectionObserver(
                    async (entries: IntersectionObserverEntry[]): Promise<void> => {
                        const entry: IntersectionObserverEntry = entries[0]

                        if (!entry.isIntersecting || busyLoadingCards) {
                            return
                        }

                        busyLoadingCards = true
                        await loadMoreEventHighlightCards().then((data: EventHighlightListResponse): void => {
                            const columns: NodeListOf<HTMLUListElement> = galleryColumns(root)
                            const columnLists: HTMLUListElement[] = Array.from(columns).filter(visibleElementsOnly)
                            const newItems = addCardsToTemplate(root, data)

                            distributeItems(newItems, columnLists)

                            const shortestColumn: HTMLUListElement = columnLists.reduce(toShortestColumn)
                            addItemToColumnList(loadingAnchor, shortestColumn)

                            busyLoadingCards = false
                        })
                    },
                    {
                        root: modalBody,
                        rootMargin: "0px 0px 200px 0px",
                    } as IntersectionObserverInit,
                )

                if (!hasMoreEntries) {
                    return
                }

                scrollObserver.observe(loadingAnchor)
                unobserveAnchorCallback = (): void => {
                    scrollObserver.unobserve(loadingAnchor)
                }
                return
            }

            if (root === eventHighlightSection()) {
                galleryToggleButton(root)?.addEventListener(
                    "click",
                    (): void => {
                        const cookieValue = eventHighlightCookie.value || {}

                        if (root?.classList.toggle("closed")) {
                            cookieValue[siteConfig.eventHighlightCookie.id] = siteConfig.eventHighlightCookie.value
                        } else {
                            delete cookieValue[siteConfig.eventHighlightCookie.id]
                            setTimeout(spreadDataToColumns, 0)

                            window.Tracking.flushDeferredTracking()
                        }

                        eventHighlightCookie.expires = Cookie.getExpireDate(3600 * 24)
                        eventHighlightCookie.value = cookieValue
                    },
                    { passive: true },
                )
            }
        })
        .catch((): void => {
            root?.parentNode?.removeChild(root)
        })
}
;((): void => {
    const highlightSection = eventHighlightSection()
    const modal: HTMLElement | null = eventHighlightModal()
    const modalBody: HTMLElement | null = galleryModalBody(modal)
    const scrollToTopButton: HTMLButtonElement | null = galleryToTopButton(modal)

    if (modal === null) {
        return
    }

    document.body.appendChild(modal)

    initializeMasonryGallery(highlightSection, () => {
        const modalGalleryData = galleryData(modal)

        for (const sectionGalleryData of galleryData(highlightSection)?.content.childNodes || []) {
            modalGalleryData?.content.append(sectionGalleryData.cloneNode(true))
        }
    })

    scrollToTopButton?.addEventListener("click", (): void => {
        modalBody?.scrollTo({ top: 0 })
    })

    $(modal).one("shown.bs.modal", (): void => {
        const scrollToTopDistanceShown = 250

        initializeMasonryGallery(modal)

        modalBody?.addEventListener(
            "scroll",
            (): void => {
                if (!modal.hasAttribute("data-scrolled") && modalBody.scrollTop > scrollToTopDistanceShown) {
                    modal.setAttribute("data-scrolled", "true")
                } else if (modal.hasAttribute("data-scrolled") && modalBody.scrollTop <= scrollToTopDistanceShown) {
                    modal.removeAttribute("data-scrolled")
                }
            },
            { passive: true },
        )
    })
})()
